import React, { useMemo } from 'react'
import { IBaseProps } from '@did/types'
import { GradientBg, TextInput, Button } from '@did/das-balance-components'
import { useDasBalanceContext } from '@did/das-app-context'
import { BaseCard, BottomSheet } from '@did/uikit'
import DidPointLogo from '@did/das-balance-assets/images/components/didpoint-logo.svg'
import { useForm } from 'react-hook-form'
import { FiatPayDialog } from '@did/stripe'
import { DasBalanceInsufficientDialog } from './das-balance-insufficient-dialog'
import { PaymentConfirm } from './payment-confirm'
import { useTopUp } from '@did/das-balance-hooks'
import { SignatureErrorDialog } from './signature-error-dialog'

interface IFormValues {
  amount: string
}

export const TopUpIndex: React.FC<IBaseProps> = () => {
  const { tt } = useDasBalanceContext()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IFormValues>()

  const {
    onTopUp,
    onPay,
    goRecordPage,
    paymentTokens,
    selectedToken,
    setSelectedToken,
    clientSecret, // stripe client secret
    // payment amount
    stripeServiceFee,
    stripePaidAmount,
    paidTokenAmount,
    setDidPointAmount,
    // loading state
    payLoading,
    // payment modal show
    selectPaymentShowing,
    setSelectPaymentShowing,
    fiatPayDialogShowing,
    setFiatPayDialogShowing,
    // error messages dialog
    dasBalanceInsufficientDialogShowing,
    signatureErrorDialogShowing,
    setDasBalanceInsufficientDialogShowing,
    setSignatureErrorDialogShowing,
    continueLoading,
    orderId
  } = useTopUp()

  const amountInputVailRegister = useMemo(() => {
    return register('amount', {
      min: {
        value: 20,
        message: tt(
          '{fieldName} must be greater than or equal to {min} {unit}',
          {
            fieldName: tt('Amount'),
            min: 20,
            unit: 'Credits'
          }
        )
      },
      max: {
        value: 100000,
        message: tt('{fieldName} must be less than or equal to {max} {unit}', {
          fieldName: tt('Amount'),
          max: 100000,
          unit: 'Credits'
        })
      },
      required: {
        value: true,
        message: tt('Invalid {fieldName}', { fieldName: tt('Amount') })
      },
      validate: {
        isDecimal: (value) =>
          /^\d+(\.\d{1,2})?$/.test(value) ||
          tt('Only 2 decimal places are supported')
      }
    })
  }, [])

  const onContinue = async (param: IFormValues) => {
    setDidPointAmount(param.amount)
    onTopUp()
  }

  const returnUrl = useMemo(() => {
    try {
      return `${
        new URL(window?.location?.href).origin
      }/balance/credits/record?orderId=${orderId}`
    } catch (e: any) {
      return ''
    }
  }, [orderId])

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-0 pt-3 z-0 relative">
      <GradientBg type="blue" />
      <div className="flex flex-col items-center mt-14">
        <DidPointLogo className="w-[66px] h-[66px]" />
        <h2 className="mt-3 mb-7 text-2xl font-bold text-tip-font-color">
          {tt('Top Up DIDPoint')}
        </h2>
      </div>
      <BaseCard className="mx-auto p-6 sm:p-8 w-full sm:w-[520px]">
        <form autoComplete="off" onSubmit={handleSubmit(onContinue)}>
          <label className="mb-2 block font-sm font-normal leading-4 text-[#11142d]">
            {tt('Amount')}
          </label>
          <TextInput
            type="number"
            pattern="[0-9]*"
            suffix="Credits"
            autoComplete="off"
            placeholder={tt('Please enter amount')}
            errorMessages={[errors?.amount?.message || '']}
            {...amountInputVailRegister}
          />
          <Button
            className="mt-16"
            type="submit"
            isLoadingGradient={false}
            black
            large
            block
            loading={continueLoading}
          >
            {!continueLoading && tt('Continue')}
          </Button>
        </form>
      </BaseCard>
      <BottomSheet
        title={<div className=" px-3 sm:px-0">{tt('Select payment')}</div>}
        open={selectPaymentShowing}
        onClose={() => {
          setSelectPaymentShowing(false)
        }}
      >
        <PaymentConfirm
          onTokenChange={setSelectedToken}
          selectedToken={selectedToken}
          stripePaidAmount={stripePaidAmount}
          stripeServiceFee={stripeServiceFee}
          paidTokenAmount={paidTokenAmount}
          tokenList={paymentTokens}
          fiatTermsLink="https://topdid.com/terms.html"
          onPay={onPay}
          payLoading={payLoading}
        />
      </BottomSheet>
      <FiatPayDialog
        showing={fiatPayDialogShowing}
        title={tt('Pay to Top Up')}
        onClose={() => {
          setFiatPayDialogShowing(false)
        }}
        returnUrl={returnUrl}
        clientSecret={clientSecret!}
        orderId={orderId}
        successCallback={() => {
          goRecordPage(orderId)
        }}
      />
      <DasBalanceInsufficientDialog
        showing={dasBalanceInsufficientDialogShowing}
        onClose={() => {
          setDasBalanceInsufficientDialogShowing(false)
        }}
        registrationFees={paidTokenAmount}
      />
      <SignatureErrorDialog
        showing={signatureErrorDialogShowing}
        onClose={() => {
          setSignatureErrorDialogShowing(false)
        }}
      />
    </div>
  )
}
